import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, MapPin, ChevronRight as Arrow, X } from "lucide-react";
import { CATEGORIES } from "../data/data";
 
export default function ExploreSection() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [activeCity, setActiveCity] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null); // Modal ke liye state (gallery index)

  const scrollerRef = useRef(null);
  const cityScrollerRef = useRef(null);

  const category = CATEGORIES[activeCategory];
  const displayData = category?.cities?.[activeCity] || category?.default || category?.cities?.[0];

  const galleryImages = displayData
    ? [displayData.image, ...(displayData.gallery || [])].filter(
        (src, i, arr) => src && arr.indexOf(src) === i
      )
    : [];

  const showNextImage = () =>
    setSelectedIndex((i) => (i + 1) % galleryImages.length);
  const showPrevImage = () =>
    setSelectedIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
 
  const handleCategoryClick = (index) => {
    setActiveCategory(index);
    setActiveCity(0);
  };
 
  const scrollByCards = (direction) => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollBy({ left: direction * 220, behavior: "smooth" });
    }
  };
 
  return (
    <section className="relative z-10 w-full max-w-[1280px] mx-auto bg-white p-4 md:p-6 rounded-3xl font-poppins select-none pt-10 shadow-lg">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
 
        .font-poppins {
          font-family: 'Poppins', sans-serif;
        }
 
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
 
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in { animation: fadeIn 0.25s ease-out forwards; }
 
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .slide-in { animation: slideIn 0.2s ease-out forwards; }
 
        /* Purple ring card ke ANDAR draw hota hai, isliye scroll box se kabhi kat-ta nahi */
        .ring-inner {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          z-index: 20;
          box-shadow: inset 0 0 0 var(--ring, 3.5px) #7e22ce;
        }
        /* Mobile city card: thodi moti ring */
        .ring-city { --ring: 4.5px; }
        @media (min-width: 1024px) { .ring-city { --ring: 3.5px; } }
      `}</style>
 
      {/* ── Top Category Scroll Bar ── */}
      <div className="relative mb-5 w-[calc(100%+1rem)] sm:w-full flex items-center gap-2">
        <button
          type="button"
          onClick={() => scrollByCards(-1)}
          className="hidden sm:flex shrink-0 z-10 h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-slate-700 hover:text-purple-600 transition-all border border-slate-100 cursor-pointer"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
 
        <div
          ref={scrollerRef}
          className="no-scrollbar flex-1 flex gap-2 sm:gap-3 overflow-x-auto scroll-smooth py-2 px-1.5"
        >
          {CATEGORIES.map((cat, index) => {
            const isActive = index === activeCategory;
            return (
              <button
                type="button"
                key={cat.id || index}
                onClick={() => handleCategoryClick(index)}
                className={`relative w-[170px] sm:w-[210px] shrink-0 h-[86px] sm:h-[68px] rounded-[20px] sm:rounded-2xl overflow-hidden text-center transition-all duration-150 focus:outline-none cursor-pointer box-border ${
                  isActive
                    ? "shadow-sm scale-[1.02]"
                    : "opacity-80 hover:opacity-100 hover:scale-[1.01]"
                }`}
                style={{
                  backgroundImage: `url(${cat.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <span className="absolute inset-0 bg-black/55" />
                {isActive && <span className="ring-inner" />}
                <span className="relative z-10 flex h-full flex-col items-center justify-center px-2">
                  <span className="text-white font-semibold sm:font-bold text-[13px] sm:text-[11px] leading-tight">
                    {cat.label}
                  </span>
                  {cat.sublabel && (
                    <span className="text-white sm:text-white/85 font-medium text-[13px] sm:text-[9.5px] leading-tight mt-0.5">
                      {cat.sublabel}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
 
        <button
          type="button"
          onClick={() => scrollByCards(1)}
          className="hidden sm:flex shrink-0 z-10 h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-slate-700 hover:text-purple-600 transition-all border border-slate-100 cursor-pointer"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
 
      {/* ── Main Layout ── */}
      <div className="flex flex-col lg:flex-row items-start gap-0 w-full">
 
        {/* ── LEFT: Sub-category List ── */}
        <div
          key={activeCategory}
          className="slide-in w-[calc(100%+1rem)] sm:w-full lg:w-[230px] shrink-0 flex flex-col mb-4 lg:mb-0"
        >
          <div className="relative lg:overflow-hidden rounded-2xl">
            <div
              ref={cityScrollerRef}
              className="flex-1 min-w-0 flex flex-row lg:flex-col gap-2.5 lg:max-h-[452px] overflow-x-auto lg:overflow-x-hidden overflow-y-hidden lg:overflow-y-auto no-scrollbar px-1.5 py-3 lg:p-1.5 lg:pb-6"
            >
              {category?.cities?.map((c, index) => {
                const isActive = index === activeCity;
                return (
                  <button
                    type="button"
                    key={c.name || index}
                    onClick={() => setActiveCity(index)}
                    className={`relative w-[95%] sm:w-[320px] lg:w-full h-[210px] lg:h-[140px] rounded-[28px] lg:rounded-2xl overflow-hidden text-left transition-all duration-200 shrink-0 focus:outline-none group cursor-pointer box-border ${
                      isActive
                        ? "shadow-md"
                        : "hover:opacity-95"
                    }`}
                    style={{
                      backgroundImage: `url(${c.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    {isActive && <span className="ring-inner ring-city" />}
 
                    {isActive && (
                      <span className="hidden lg:block absolute top-2.5 right-2.5 z-10 h-2.5 w-2.5 rounded-full bg-purple-400 shadow-md" />
                    )}
 
                    <div className="absolute bottom-9 left-5 right-5 lg:bottom-3 lg:left-3.5 lg:right-8 z-10 text-white">
                      <p className="font-bold text-xl lg:text-sm leading-tight">{c.name}</p>
                      <span className="flex items-center gap-1 text-base lg:text-xs text-white/80 mt-1">
                        <MapPin className="h-4 w-4 lg:h-3 lg:w-3 shrink-0 text-white" />
                        <span className="truncate">{c.location}</span>
                      </span>
                    </div>
 
                    <div className={`hidden lg:block absolute right-2.5 bottom-2.5 z-10 transition-opacity duration-150 ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                      <Arrow className="h-4 w-4 text-white" />
                    </div>
                  </button>
                );
              })}
            </div>
 
            <div className="hidden lg:block pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent z-20" />
          </div>
        </div>
 
        {/* ── Middle Divider ── */}
        <div className="hidden lg:flex items-center justify-center self-stretch px-3">
          <div className="w-[2px] bg-purple-200/80 rounded-full h-full my-1" />
        </div>
 
        {/* ── RIGHT: Details Panel ── */}
        {displayData && (
          <div
            key={`${activeCategory}-${activeCity}`}
            className="fade-in flex-1 w-full rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-sm flex flex-col"
          >
            {/* Main Banner Image (Click karne par modal khulega) */}
            <div
              className="w-full h-[150px] sm:h-[230px] overflow-hidden relative cursor-pointer"
              onClick={() => setSelectedIndex(0)}
            >
              <img
                src={displayData.image}
                alt={displayData.name}
                className="w-full h-full object-cover object-center"
              />
            </div>
 
            {/* Details */}
            <div className="p-5 flex-1 flex flex-col justify-between gap-4">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  {displayData.name}
                </h3>
                <p className="mt-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                  <MapPin className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                  {displayData.location}
                </p>
 
                <div className="mt-4">
                  <p className="font-bold text-slate-900 text-xs sm:text-sm">About</p>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    {displayData.about}
                  </p>
                </div>
              </div>
 
              {/* Best Time + Gallery Thumbnails */}
              <div className="contents sm:flex sm:flex-wrap sm:items-end sm:justify-between sm:gap-3 sm:pt-1">
                <div className="order-1 sm:order-none">
                  <p className="font-bold text-slate-900 text-xs sm:text-sm">Best Time to Visit</p>
                  <p className="mt-0.5 text-xs text-slate-600">{displayData.bestTime}</p>
                </div>
 
                {/* Thumbnails (Click karne par modal me dikhenge) */}
                <div className="order-3 sm:order-none flex gap-2 w-full sm:w-auto sm:shrink-0">
                  {(displayData.gallery || [displayData.image, displayData.image, displayData.image]).slice(0, 4).map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`${displayData.name} ${i + 1}`}
                      onClick={() => setSelectedIndex(galleryImages.indexOf(src))}
                      className={`${i === 3 ? "sm:hidden " : ""}flex-1 min-w-0 sm:flex-none h-14 sm:w-16 rounded-lg object-cover shadow-xs cursor-pointer hover:opacity-85 transition-opacity`}
                    />
                  ))}
                </div>
              </div>
 
              {/* Highlights */}
              <div className="order-2 sm:order-none">
                <p className="font-bold text-slate-900 text-xs sm:text-sm mb-2">Highlights</p>
                <div className="flex flex-wrap gap-1.5">
                  {(displayData.highlights || []).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-[#faedff] px-3.5 py-1 text-xs font-bold text-purple-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
 
      {/* ── IMAGE POPUP / MODAL LIGHTBOX ──
          createPortal se modal body me render hota hai, isliye navbar ke upar aata hai.
          Prev / Next buttons image ke bilkul paas hain (same row me). */}
      {selectedIndex !== null &&
        createPortal(
          <div
            className="font-poppins fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-2 sm:p-4 fade-in"
            onClick={() => setSelectedIndex(null)}
          >
            <div className="flex w-full max-w-[800px] items-center justify-center gap-1 sm:gap-4">
              {/* Prev Button */}
              {galleryImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showPrevImage();
                  }}
                  className="shrink-0 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-transparent text-white sm:bg-white sm:text-slate-800 sm:shadow-md sm:hover:bg-slate-100 transition-all cursor-pointer"
                >
                  <ChevronLeft className="h-6 w-6 sm:h-5 sm:w-5" />
                </button>
              )}

              {/* Fixed size box: har image same size me aayegi */}
              <div
                className="relative flex-1 min-w-0 max-w-2xl h-[190px] sm:h-[70vh] rounded-3xl sm:rounded-2xl overflow-hidden shadow-2xl bg-black"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  type="button"
                  onClick={() => setSelectedIndex(null)}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-white text-slate-800 sm:bg-black/50 sm:text-white sm:hover:bg-black/70 transition-all cursor-pointer sm:backdrop-blur-sm"
                >
                  <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>

                {/* Modal Image */}
                <img
                  key={selectedIndex}
                  src={galleryImages[selectedIndex]}
                  alt="Preview"
                  className="w-full h-full object-cover object-center block fade-in"
                />
              </div>

              {/* Next Button */}
              {galleryImages.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    showNextImage();
                  }}
                  className="shrink-0 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-transparent text-white sm:bg-white sm:text-slate-800 sm:shadow-md sm:hover:bg-slate-100 transition-all cursor-pointer"
                >
                  <ChevronRight className="h-6 w-6 sm:h-5 sm:w-5" />
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </section>
  );
}